import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import ClientPicker from '../ClientPicker';
import { useClient, useSaveLastClientPreference, useUserPreferences } from '../../services/conversionsApi';
import HeaderAccuracyBadge from './HeaderAccuracyBadge';
import { isClientId, isClientScoped } from '../../lib/clientContext';

/**
 * THE client picker (docs/client-context.md): one, in the header, on every screen. The current client is `?clientId=`
 * on the current URL; this control reads and writes it, and remembers it per person (`lastClientId`).
 *
 * - On a client-scoped screen, picking sets `clientId` by `replace` (a selection refines the screen; it is not a new
 *   history entry), and clearing removes it AND the memory, so the next bare open is genuinely empty.
 * - A client-scoped screen opened with no `clientId` applies the remembered client once it resolves, by `replace`, so
 *   the URL says what the screen shows. A remembered id that does not resolve is never applied.
 * - An explicit `clientId` (a link, a typed URL) always wins, and becomes the remembered one.
 * - On a client-free screen (Home, What's new) it shows the remembered client, and a pick only remembers it: the nav
 *   then carries it to whichever screen comes next.
 */
export default function HeaderClientPicker() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [params, setParams] = useSearchParams();
  const prefs = useUserPreferences();
  const { mutate: saveLastClient } = useSaveLastClientPreference();

  const scoped = isClientScoped(pathname);
  const urlClient = params.get('clientId') ?? '';
  const remembered = prefs.data?.lastClientId ?? '';
  const rememberedClient = useClient(scoped && !urlClient ? remembered : '');
  const current = scoped ? urlClient : urlClient || remembered;

  useEffect(() => {
    if (!scoped || urlClient || !remembered || !rememberedClient.data) return;
    const next = new URLSearchParams(params);
    next.set('clientId', remembered);
    setParams(next, { replace: true });
  }, [scoped, urlClient, remembered, rememberedClient.data, params, setParams]);

  useEffect(() => {
    if (!prefs.isSuccess || !urlClient || !isClientId(urlClient)) return;
    if (urlClient.toLowerCase() !== remembered.toLowerCase()) saveLastClient(urlClient.toLowerCase());
  }, [prefs.isSuccess, urlClient, remembered, saveLastClient]);

  const onChange = (next: string) => {
    if (!next) saveLastClient(null);
    if (!scoped) {
      if (next) saveLastClient(next.toLowerCase());
      return;
    }
    // Every OTHER param belongs to the previous client's view (a journal handoff, a filter), so a new client starts the
    // screen clean: only clientId survives. The path is kept, so the bookkeeper stays on the same screen.
    setParams(next ? new URLSearchParams({ clientId: next }) : new URLSearchParams(), { replace: true });
  };

  return (
    <div className="app-header-client">
      <ClientPicker value={current} onChange={onChange} label={t('clientContext.headerLabel')} compact />
      {/* Beside the client's name, admins only: its accuracy in counts (accuracy spec §3; ptc-docs plan 2026-11-02 §2). */}
      <HeaderAccuracyBadge clientId={current} />
    </div>
  );
}
